import { useNavigate } from "react-router";
import { path } from "../../../utils/common/constant";
import { useState } from "react";
import { Clock, List, MessageCircle, Lock } from "lucide-react";
import urgentVisa from "../../../assets/images/urgent-visa-application.jpg";
import { useTranslation } from "react-i18next";

const EmergencyInquiryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showTableOfContents, setShowTableOfContents] = useState(false);

  const handleScrollToLabel = (label: string) => {
    const element = document.getElementById(label);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <div>
      <div className="w-full py-8 px-4 text-center text-white bg-gradient-to-b from-[#F00D0D] to-[#994F4F]">
        <h1 className="text-[32px] font-bold mb-2">
          {t("emergency_visa.title")}
        </h1>
        <p className="text-[22px] font-bold">{t("emergency_visa.subtitle")}</p>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20 flex flex-col md:flex-row justify-between gap-8">
        {/* Main content column */}
        <div className="flex flex-col justify-center items-center gap-10 md:w-2/3">
          {/* Table of contents */}
          <div className="w-full flex flex-col gap-4 border rounded-lg p-4 bg-white shadow-md">
            <p className="text-[22px] text-primary-blue font-bold flex items-center gap-2">
              <List
                className="cursor-pointer"
                onClick={() => setShowTableOfContents(!showTableOfContents)}
              />
              {t("emergency_visa.table_of_contents.title")}
            </p>
            {showTableOfContents && (
              <ul className="list-decimal ml-8 space-y-8">
                <li
                  className="underline cursor-pointer hover:text-primary-blue"
                  onClick={() => handleScrollToLabel("1")}
                >
                  {t("emergency_visa.table_of_contents.1")}
                </li>
                <li
                  className="underline cursor-pointer hover:text-primary-blue"
                  onClick={() => handleScrollToLabel("2")}
                >
                  {t("emergency_visa.table_of_contents.2")}
                </li>
                <li
                  className="underline cursor-pointer hover:text-primary-blue"
                  onClick={() => handleScrollToLabel("3")}
                >
                  {t("emergency_visa.table_of_contents.3")}
                </li>
              </ul>
            )}
          </div>

          <div className="w-full flex flex-col justify-center items-center gap-8">
            <p
              id="1"
              className="w-full text-[22px] text-primary-blue font-bold"
            >
              {t("emergency_visa.table_of_contents.1")}
            </p>
            <p className="w-full text-justify">
              {t("emergency_visa.1.content1")}
            </p>
            <p className="w-full text-justify">
              {t("emergency_visa.1.content2")}
            </p>

            <img
              src={urgentVisa}
              alt="Urgent Vietnam Visa"
              className="w-full h-auto rounded-lg"
              loading="lazy"
            />

            <p
              id="2"
              className="w-full text-[22px] text-primary-blue font-bold"
            >
              {t("emergency_visa.table_of_contents.2")}
            </p>
            <ul className="list-disc w-full ml-16">
              <li>{t("emergency_visa.2.content1.1")}</li>
              <li>{t("emergency_visa.2.content1.2")}</li>
              <li>{t("emergency_visa.2.content1.3")}</li>
            </ul>
            <p className="w-full text-justify">
              {t("emergency_visa.2.content2")}{" "}
              <span className="font-semibold italic">
                {t("home.processing_plan.plan3.title")}
              </span>
            </p>

            <p
              id="3"
              className="w-full text-[22px] text-primary-blue font-bold"
            >
              {t("emergency_visa.table_of_contents.3")}
            </p>
            <ul className="list-disc w-full ml-16">
              <li>
                <span className="font-semibold">{t("home.steps.step1")}: </span>{" "}
                {t("emergency_visa.3.content1")}
              </li>
              <li>
                <span className="font-semibold">{t("home.steps.step2")}: </span>{" "}
                {t("emergency_visa.3.content2")}
              </li>
              <li>
                <span className="font-semibold">{t("home.steps.step3")}: </span>{" "}
                {t("emergency_visa.3.content3")}
              </li>
            </ul>

            <button
              type="button"
              className="hidden sm:flex bg-primary-lightyellow hover:bg-primary-yellow text-primary-blue border-none px-8 py-4 rounded-full font-semibold"
              onClick={() => {
                navigate("../" + path.VISA_APPLICATION, {
                  state: {
                    processingTime: "Urgent",
                    typeVisa: "OneMonthSingle",
                  },
                });
              }}
            >
              {t("emergency_visa.cta.register_now")}
            </button>
          </div>
        </div>

        {/* Sidebar column */}
        <div className="md:w-1/3 flex flex-col gap-6">
          {/* Why apply with Us? section */}
          <div className="bg-primary-blue text-white rounded-lg p-6 shadow-md">
            <h2 className="text-2xl font-bold mb-6">{t("emergency_visa.side_bar.title")}</h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-2">
                <span className="text-white mt-1">•</span>
                <span>{t("emergency_visa.side_bar.1")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-white mt-1">•</span>
                <span>{t("emergency_visa.side_bar.2")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-white mt-1">•</span>
                <span>{t("emergency_visa.side_bar.3")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-white mt-1">•</span>
                <span>{t("emergency_visa.side_bar.4")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-white mt-1">•</span>
                <span>{t("emergency_visa.side_bar.5")}</span>
              </li>
            </ul>
            <button
              type="button"
              className="mt-6 w-full bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2"
              onClick={() => {
                navigate("../" + path.VISA_APPLICATION, {
                  state: {
                    processingTime: "Urgent",
                    typeVisa: "OneMonthSingle",
                  },
                });
              }}
            >
              <Lock size={20} /> {t("emergency_visa.side_bar.apply_now")}
            </button>
          </div>

          {/* Need more help? section */}
          <div className="bg-white rounded-lg p-6 shadow-md border">
            <h2 className="text-xl font-bold mb-4">{t("emergency_visa.side_bar.title_help")}</h2>
            <div className="border-b pb-3">
              <p className="text-gray-700">{t("emergency_visa.side_bar.contact_us")}</p>
            </div>
            <button
              type="button"
              className="mt-4 w-full bg-primary-yellow text-white px-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2"
              onClick={() => {
                // Navigate to contact page
                navigate("../" + path.CONTACT);
              }}
            >
              {t("emergency_visa.side_bar.question")} <MessageCircle size={20} />
            </button>
            <p className="text-sm text-gray-700 mt-4">
              {t("emergency_visa.side_bar.support_247")}
            </p>
            <div className="mt-4 flex items-center text-gray-700">
              <Clock size={18} className="mr-2" /> {t("emergency_visa.side_bar.support_service")}
            </div>
            {/* <div className="mt-4 flex items-center text-gray-700">
              <span className="font-semibold">Excellent 5</span>
              <span className="mx-1">out of 5 on</span>
              <a href="#" className="text-blue-600 font-semibold">
                Tripadvisor
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyInquiryPage;
